import React, { useEffect, useMemo, useState } from "react";

import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ChartColumn,
  Layers,
  X,
} from "lucide-react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";

import CommonModal from "../../../../Components/Modal";
import TabsComponent from "../../../../Components/Tab";
import { useOrgSyntheticDataToggle } from "../../../../Hooks/useOrgSyntheticDataToggle";
import { getCategoryLabel } from "../../../../intelligence-briefing-library/constants/kpiCategories";
import type {
  AccountMatrixData,
  HawkeyeChartMetricsProps,
  HawkeyeMetricsAccountsInput,
  MetricValue,
} from "../../../../intelligence-briefing-library/hooks/useHawkeyeMetrics";
import { useHawkeyeMetrics } from "../../../../intelligence-briefing-library/hooks/useHawkeyeMetrics";
import { useAppDispatch, useAppSelector } from "../../../../Store/Hooks";
import { fetchHealthScoreThresholds } from "../../../../Store/Reducers/VendorAdminSlice";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../../Utils/HealthThresholds";
import { businessHealthColor } from "../../../../Utils/Helper/businessHealth";
import { formatActualValueWithUnit } from "../../../../Utils/Helper/formatHawkeyeMetricValue";

import "react-circular-progressbar/dist/styles.css";

type KpiTab = "metrics" | "categories";

export type KpiCategoryItem = {
  id: string;
  label: string;
  score: number;
  trendLabel: string;
  trendDirection: "up" | "down" | "flat";
};

export type KpiMetricItem = {
  id: string;
  label: string;
  value: string;
};

export type AccountHawkeyeMetrics = HawkeyeChartMetricsProps & {
  measurementDate: string | null;
};

type UseAccountHawkeyeMetricsArgs = {
  accountId: string | undefined;
  accountName?: string | null;
};

export const useAccountHawkeyeMetrics = ({
  accountId,
  accountName,
}: UseAccountHawkeyeMetricsArgs): AccountHawkeyeMetrics => {
  const accountsInput = useMemo((): HawkeyeMetricsAccountsInput | undefined => {
    if (!accountId) return undefined;

    const account = {
      id: accountId,
      name: accountName?.trim() || "Account",
    };

    return {
      selectedAccounts: [account],
      allWatchlistAccounts: [account],
      watchlistId: null,
      selectedMeasurementDate: null,
      latestMeasurementDate: null,
    };
  }, [accountId, accountName]);

  const {
    matrixData,
    loading,
    error,
    configuration,
    updateConfiguration,
    availableCategories,
    orderedCategories,
    refetch,
    reloadConfiguration,
  } = useHawkeyeMetrics(accountsInput, undefined, null, {
    requireDate: false,
  });

  const measurementDate = useMemo(
    () => matrixData[0]?.digestMeta?.measurement_date ?? null,
    [matrixData]
  );

  return useMemo(
    () => ({
      matrixData,
      loading,
      error,
      configuration,
      updateConfiguration,
      availableCategories,
      orderedCategories,
      refetch,
      reloadConfiguration,
      measurementDate,
    }),
    [
      matrixData,
      loading,
      error,
      configuration,
      updateConfiguration,
      availableCategories,
      orderedCategories,
      refetch,
      reloadConfiguration,
      measurementDate,
    ]
  );
};

const CATEGORY_SHORT_LABELS: Record<string, string> = {
  health: "Account Health",
  financial: "Fin. Perf.",
  experience: "Cust. Expr.",
  engagement: "Prod. Eng.",
  adoption: "Adoption",
  support: "Support",
  relationships: "Relationships",
  support_risk: "Risk mitigation",
};

const getCategoryShortLabel = (categoryId: string): string =>
  CATEGORY_SHORT_LABELS[categoryId] ||
  getCategoryLabel(categoryId).split(" ").slice(0, 2).join(" ");

const parseNumeric = (raw: string | undefined): number | null => {
  if (raw == null || raw.trim() === "") return null;
  const parsed = parseFloat(String(raw).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const isEmptyOrZeroMetricValue = (value: string): boolean => {
  const trimmed = (value || "").trim();
  if (!trimmed || trimmed === "NA" || trimmed === "N/A") return true;
  const numeric = parseNumeric(trimmed);
  if (numeric != null) return numeric === 0;
  return false;
};

const getMetricChangePercent = (metric: MetricValue): number | null => {
  if (
    metric.change_percentage != null &&
    Number.isFinite(metric.change_percentage)
  ) {
    return metric.change_percentage;
  }

  const current = parseNumeric(metric.value);
  const previous = parseNumeric(metric.previous_value);
  if (current == null || previous == null) return null;
  if (previous === 0) {
    if (current === 0) return 0;
    return current > 0 ? 100 : -100;
  }
  return ((current - previous) / Math.abs(previous)) * 100;
};

const getCategoryTrend = (
  metrics: MetricValue[]
): Pick<KpiCategoryItem, "trendLabel" | "trendDirection"> => {
  const changes = metrics
    .map(getMetricChangePercent)
    .filter((n): n is number => n != null && Number.isFinite(n));

  if (!changes.length) {
    return { trendLabel: "—", trendDirection: "flat" };
  }

  const avg = changes.reduce((sum, n) => sum + n, 0) / changes.length;
  const rounded = Math.round(avg * 10) / 10;
  const absLabel = Math.abs(rounded).toFixed(1);

  if (Math.abs(rounded) < 0.1) {
    return { trendLabel: absLabel, trendDirection: "flat" };
  }
  if (rounded > 0) {
    return { trendLabel: absLabel, trendDirection: "up" };
  }
  return { trendLabel: absLabel, trendDirection: "down" };
};

const formatMetricItem = (metric: MetricValue): KpiMetricItem => ({
  id: metric.metric_id,
  label: metric.metric_name || metric.metric_id,
  // Same Actual Value display as Always On Channel BubbleCell
  value: formatActualValueWithUnit(metric.value, metric.unit_of_measure),
});

const buildKpiMetrics = (
  accountRow: AccountMatrixData | undefined,
  includedMetrics: Record<string, string[]>,
  orderedCategories: string[]
): KpiMetricItem[] => {
  if (!accountRow) return [];

  const items: KpiMetricItem[] = [];
  const seen = new Set<string>();

  for (const categoryId of orderedCategories) {
    const metricIds = includedMetrics[categoryId] || [];
    for (const metricId of metricIds) {
      if (seen.has(metricId)) continue;
      const metric = accountRow.rawMetrics.find(m => m.metric_id === metricId);
      if (!metric) continue;
      seen.add(metricId);
      items.push(formatMetricItem(metric));
    }
  }

  for (const metric of accountRow.rawMetrics) {
    if (seen.has(metric.metric_id)) continue;
    seen.add(metric.metric_id);
    items.push(formatMetricItem(metric));
  }

  items.sort((a, b) => {
    const aLow = isEmptyOrZeroMetricValue(a.value);
    const bLow = isEmptyOrZeroMetricValue(b.value);
    if (aLow === bLow) return 0;
    return aLow ? 1 : -1;
  });

  return items;
};

const buildKpiCategories = (
  accountRow: AccountMatrixData | undefined
): KpiCategoryItem[] => {
  if (!accountRow) return [];

  return accountRow.categoryScores
    .filter(
      cat =>
        cat.category !== "overall" &&
        (cat.metrics.length > 0 || Number.isFinite(cat.score))
    )
    .map(cat => {
      const trend = getCategoryTrend(cat.metrics);
      return {
        id: cat.category,
        label: getCategoryShortLabel(cat.category),
        score: Math.round(cat.score),
        trendLabel: trend.trendLabel,
        trendDirection: trend.trendDirection,
      };
    });
};

const accountRowHasKpiData = (
  accountRow: AccountMatrixData | undefined
): boolean =>
  Boolean(
    accountRow?.hasData ||
      accountRow?.hasDigest ||
      (accountRow?.rawMetrics.length ?? 0) > 0
  );

export const useAccountKpiDisplay = ({
  hawkeye,
}: {
  hawkeye: AccountHawkeyeMetrics;
}) => {
  const accountRow = hawkeye.matrixData[0];

  const metrics = useMemo(
    () =>
      buildKpiMetrics(
        accountRow,
        hawkeye.configuration.includedMetrics,
        hawkeye.orderedCategories
      ),
    [
      accountRow,
      hawkeye.configuration.includedMetrics,
      hawkeye.orderedCategories,
    ]
  );

  const categories = useMemo(
    () => buildKpiCategories(accountRow),
    [accountRow]
  );

  const hasData = useMemo(() => accountRowHasKpiData(accountRow), [accountRow]);

  return {
    loading: hawkeye.loading,
    error: hawkeye.error,
    metrics,
    categories,
    hasData,
  };
};

type AccountWorkspaceKpisProps = {
  hawkeye: AccountHawkeyeMetrics;
  className?: string;
};

const PREVIEW_COUNT = 6;
const KPI_PREVIEW_GRID_CLASS =
  "grid h-full min-h-0 flex-1 grid-cols-2 grid-rows-3 gap-2.5";
const KPI_MODAL_ROW_HEIGHT_CLASS = "auto-rows-[80px]";
const KPI_CARD_CLASS =
  "box-border flex h-full min-h-0 max-h-full overflow-hidden rounded-lg border border-[#E8EAEB]";
const KPI_MODAL_CLASS =
  "flex h-[80vh] min-h-[72vh] max-h-[90vh] flex-col overflow-hidden";
const KPI_MODAL_BODY_CLASS = "min-h-0 flex-1 overflow-y-auto px-5 py-4";

type KpiGridVariant = "preview" | "modal";

const KPI_TABS = [
  {
    id: "metrics",
    label: "Actual Metrics",
    icon: <ChartColumn size={14} strokeWidth={1.75} />,
  },
  {
    id: "categories",
    label: "Categories",
    icon: <Layers size={14} strokeWidth={1.75} />,
  },
];

const trendColorClass = (direction: KpiCategoryItem["trendDirection"]) => {
  if (direction === "up") return "text-[#01BA10]";
  if (direction === "down") return "text-[#E01E21]";
  return "text-[#6B7280]";
};

const AccountWorkspaceKpis: React.FC<AccountWorkspaceKpisProps> = ({
  hawkeye,
  className = "",
}) => {
  const { loading, error, metrics, categories, hasData } = useAccountKpiDisplay(
    { hawkeye }
  );
  const { useSyntheticData } = useOrgSyntheticDataToggle();
  const dispatch = useAppDispatch();
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );

  const [tab, setTab] = useState<KpiTab>("metrics");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<KpiTab>("metrics");

  const previewMetrics = metrics.slice(0, PREVIEW_COUNT);
  const previewCategories = categories.slice(0, PREVIEW_COUNT);

  useEffect(() => {
    void dispatch(fetchHealthScoreThresholds());
  }, [dispatch]);

  useEffect(() => {
    if (modalOpen) setModalTab(tab);
  }, [modalOpen, tab]);

  const syntheticChip = useSyntheticData ? (
    <span
      className="truncate rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium leading-none text-amber-900"
      role="status"
      title="Synthetic Data Mode is enabled. KPIs and metrics displayed are based on sample data, not live production data."
    >
      Synthetic data mode is ON - showing sample KPIs
    </span>
  ) : null;

  const gridClassName = (variant: KpiGridVariant) =>
    variant === "modal"
      ? `grid grid-cols-2 gap-2.5 ${KPI_MODAL_ROW_HEIGHT_CLASS} items-stretch`
      : KPI_PREVIEW_GRID_CLASS;

  const renderMetricsGrid = (
    items: KpiMetricItem[],
    variant: KpiGridVariant = "preview"
  ) => (
    <div className={gridClassName(variant)}>
      {items.map(metric => (
        <div
          key={metric.id}
          className={`${KPI_CARD_CLASS} flex-col justify-center px-3 py-3`}
        >
          <p className="m-0 shrink-0 truncate text-base font-semibold leading-5 text-[#01BA10]">
            {metric.value}
          </p>
          <p className="m-0 mt-1 min-h-0 line-clamp-2 text-xs font-medium leading-[15px] text-[#959595]">
            {metric.label}
          </p>
        </div>
      ))}
      {items.length === 0 && (
        <p className="col-span-2 m-0 text-sm text-[#9F9F9F]">
          No metrics available.
        </p>
      )}
    </div>
  );

  const renderCategoriesGrid = (
    items: KpiCategoryItem[],
    variant: KpiGridVariant = "preview"
  ) => (
    <div className={gridClassName(variant)}>
      {items.map(category => {
        const clamped = Math.min(100, Math.max(0, category.score));
        const gaugeColor =
          businessHealthColor(clamped, bhThresholds) ?? "#ef4444";

        return (
          <div
            key={category.id}
            className={`${KPI_CARD_CLASS} items-center justify-between gap-1 px-2.5 py-3`}
          >
            <div className="min-w-0 flex-1 pr-1">
              <p
                className={`m-0 flex items-center gap-1 text-xs font-semibold leading-[15px] ${trendColorClass(category.trendDirection)}`}
              >
                {category.trendDirection === "up" && (
                  <ArrowUp className="size-3.5 shrink-0" aria-hidden />
                )}
                {category.trendDirection === "down" && (
                  <ArrowDown className="size-3.5 shrink-0" aria-hidden />
                )}
                {category.trendDirection === "flat" && (
                  <ArrowRight className="size-3.5 shrink-0" aria-hidden />
                )}
                <span className="truncate">
                  {category.trendDirection === "up" &&
                  category.trendLabel !== "—"
                    ? `+${category.trendLabel}`
                    : category.trendDirection === "down"
                      ? `-${category.trendLabel}`
                      : category.trendLabel}
                </span>
              </p>
              <p className="m-0 mt-1 line-clamp-2 text-[13px] font-medium leading-4 text-[#6B7280]">
                {category.label}
              </p>
            </div>
            <div
              className="flex size-14 shrink-0 items-center justify-center"
              aria-label={`Score ${Math.round(clamped)}`}
            >
              <CircularProgressbar
                value={clamped}
                text={`${Math.round(clamped)}`}
                styles={buildStyles({
                  pathColor: gaugeColor,
                  textColor: "#111827",
                  trailColor: "#E9EDF0",
                  textSize: "30px",
                })}
              />
            </div>
          </div>
        );
      })}
      {items.length === 0 && (
        <p className="col-span-2 m-0 text-sm text-[#9F9F9F]">
          No categories available.
        </p>
      )}
    </div>
  );

  const renderTabs = (
    activeTab: KpiTab,
    onTabChange: (next: KpiTab) => void
  ) => (
    <TabsComponent
      tabs={KPI_TABS}
      activeTab={activeTab}
      onTabChange={(id: string) => onTabChange(id as KpiTab)}
      variant="tab-underline"
      segmentedLayout="fit"
      className="mb-0 rounded-full bg-white"
    />
  );

  return (
    <>
      <section
        className={`box-border flex h-full w-full min-w-0 flex-col overflow-hidden rounded-lg border border-[#EBEBEB] bg-white px-4 pb-3 pt-3.5 ${className}`}
      >
        <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-3">
          <div className="flex shrink-0 min-h-6 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <h2 className="m-0 shrink-0 text-base font-bold leading-6 text-black">
                KPIs
              </h2>
              {syntheticChip}
            </div>
            {hasData ? (
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="shrink-0 border-0 bg-transparent p-0 text-sm font-semibold leading-6 text-[#7C3AED] hover:underline"
              >
                See All
              </button>
            ) : (
              <span
                className="invisible shrink-0 text-sm font-semibold leading-6"
                aria-hidden
              >
                See All
              </span>
            )}
          </div>

          <hr className="m-0 w-full shrink-0 border-0 border-t border-[#F6F6F6]" />

          <div className="shrink-0">{renderTabs(tab, setTab)}</div>

          {loading && (
            <p className="m-0 flex flex-1 items-center justify-center text-sm text-[#9F9F9F]">
              Loading KPIs…
            </p>
          )}

          {!loading && error && (
            <p className="m-0 text-sm text-[#E01E21]" role="alert">
              {error}
            </p>
          )}

          {!loading && !error && !hasData && (
            <p className="m-0 flex flex-1 items-center justify-center text-sm text-[#9F9F9F]">
              No KPIs data available.
            </p>
          )}

          {!loading &&
            !error &&
            hasData &&
            (tab === "metrics"
              ? renderMetricsGrid(previewMetrics)
              : renderCategoriesGrid(previewCategories))}
        </div>
      </section>

      <CommonModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        size="2xl"
        showHeader={false}
        className={KPI_MODAL_CLASS}
        contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
      >
        <div className="flex shrink-0 flex-col border-b border-[#EBEBEB]">
          <div className="flex items-center justify-between px-5 py-4">
            <div className="flex min-w-0 items-center gap-2">
              <h2 className="m-0 shrink-0 text-lg font-bold text-black">
                KPIs
              </h2>
              {syntheticChip}
            </div>
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="border-0 bg-transparent p-1 text-[#6B7280]"
              aria-label="Close"
            >
              <X className="size-5" />
            </button>
          </div>
          <div className="border-t border-[#F6F6F6] px-5 py-3">
            {renderTabs(modalTab, setModalTab)}
          </div>
        </div>

        <div className={KPI_MODAL_BODY_CLASS}>
          {modalTab === "metrics"
            ? renderMetricsGrid(metrics, "modal")
            : renderCategoriesGrid(categories, "modal")}
        </div>
      </CommonModal>
    </>
  );
};

export default AccountWorkspaceKpis;
